// src/utils/overlapChecker.js
// Utility functions for checking room booking overlaps

/**
 * Check if two time periods overlap
 * @param {Date} start1 - Start time of first booking
 * @param {Date} end1 - End time of first booking
 * @param {Date} start2 - Start time of second booking
 * @param {Date} end2 - End time of second booking
 * @returns {boolean} - True if overlapping, false otherwise
 */
export function doTimesOverlap(start1, end1, start2, end2) {
  // Convert to timestamps for easier comparison
  const s1 = start1.getTime();
  const e1 = end1.getTime();
  const s2 = start2.getTime();
  const e2 = end2.getTime();

  // No overlap if one booking ends before the other starts
  return !(e1 <= s2 || e2 <= s1);
}

/**
 * Extract room ID from room string format "ID : Room Name"
 * @param {string} roomString - Room string in format "ID : Room Name"
 * @returns {string} - Room ID
 */
export function extractRoomId(roomString) {
  if (!roomString || typeof roomString !== "string") return "";

  // Handle both "ID : Room Name" and just "ID" formats
  const parts = roomString.split(" : ");
  return parts[0].trim();
}

/**
 * Check for overlapping bookings in the same room
 * @param {Object} newBooking - New booking data
 * @param {Array} existingBookings - Array of existing bookings
 * @returns {Object} - Result object with overlap status and conflicting bookings
 */
export function checkRoomOverlap(newBooking, existingBookings) {
  const { Room: newRoom, startDate: newStart, endDate: newEnd } = newBooking;

  // Extract room ID from the new booking
  const newRoomId = extractRoomId(newRoom);

  // Convert new booking dates to Date objects if they're strings
  const newStartDate =
    typeof newStart === "string" ? new Date(newStart) : newStart;
  const newEndDate = typeof newEnd === "string" ? new Date(newEnd) : newEnd;

  // Validate dates
  if (
    !newStartDate ||
    !newEndDate ||
    isNaN(newStartDate.getTime()) ||
    isNaN(newEndDate.getTime())
  ) {
    return {
      hasOverlap: false,
      error: "Invalid dates provided for new booking",
    };
  }

  // Check if start time is after end time
  if (newStartDate >= newEndDate) {
    return {
      hasOverlap: false,
      error: "Start time must be before end time",
    };
  }

  const conflictingBookings = [];

  // Check against existing bookings
  for (const booking of existingBookings) {
    // Skip cancelled bookings
    if (booking.bookStatus === "cancel" || booking.bStatus === "cancel") {
      continue;
    }

    // Extract room ID from existing booking
    const existingRoomId = extractRoomId(booking.room);

    // Only check bookings for the same room
    if (newRoomId !== existingRoomId) {
      continue;
    }

    // Get existing booking dates
    let existingStart = booking.start || booking.startDate;
    let existingEnd = booking.end || booking.endDate;

    // Handle different date formats
    if (typeof existingStart === "string") {
      existingStart = new Date(existingStart);
    } else if (existingStart?.toDate) {
      existingStart = existingStart.toDate();
    }

    if (typeof existingEnd === "string") {
      existingEnd = new Date(existingEnd);
    } else if (existingEnd?.toDate) {
      existingEnd = existingEnd.toDate();
    }

    // Skip if dates are invalid
    if (
      !existingStart ||
      !existingEnd ||
      isNaN(existingStart.getTime()) ||
      isNaN(existingEnd.getTime())
    ) {
      continue;
    }

    // Check for overlap
    if (doTimesOverlap(newStartDate, newEndDate, existingStart, existingEnd)) {
      conflictingBookings.push({
        id: booking.id,
        bookBy: booking.bookBy,
        room: booking.room,
        start: existingStart,
        end: existingEnd,
        status: booking.status,
        note: booking.note,
      });
    }
  }

  return {
    hasOverlap: conflictingBookings.length > 0,
    conflictingBookings,
    roomId: newRoomId,
  };
}

/**
 * Format date for display in conflict messages
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export function formatDateForDisplay(date) {
  if (!date) return "N/A";

  return date.toLocaleDateString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/**
 * Generate user-friendly conflict message
 * @param {Array} conflictingBookings - Array of conflicting bookings
 * @param {string} roomId - Room ID
 * @returns {string} - Formatted conflict message
 */
export function generateConflictMessage(conflictingBookings, roomId) {
  if (!conflictingBookings || conflictingBookings.length === 0) {
    return "";
  }

  let message = `พบการจองที่ทับซ้อนในห้อง ${roomId}:\n\n`;

  conflictingBookings.forEach((booking, index) => {
    message += `${index + 1}. จองโดย: ${booking.bookBy || "ไม่ระบุ"}\n`;
    message += `   เวลา: ${formatDateForDisplay(
      booking.start
    )} ถึง ${formatDateForDisplay(booking.end)}\n`;
    message += `   สถานะ: ${booking.status || "ไม่ระบุ"}\n`;
    if (booking.note) {
      message += `   หมายเหตุ: ${booking.note}\n`;
      message += `   หมายเหตุ(นักศึกษา): ${booking.sNote}\n`;
    }
    message += "\n";
  });

  return message;
}
